import jwtDecode from 'jwt-decode';
import isNumber from 'lodash/isNumber';
import { useDispatch } from 'react-redux';
import Cookies from 'js-cookie';

import { store } from '../redux/store';
import { ToastPosition, TypeOptions, toast } from 'react-toastify';
import moment from 'moment-timezone';
import CryptoJS from 'crypto-js';
import {
    FeeStructure,
    IAudition,
    IContest,
    IEvent,
    IGlobalConfigurationMediaControl,
    INuevoPlaylist,
    IProject,
    IRole,
    IThspianPlaylist,
    IUpNext,
    IUserSummary,
    IWorkProfile,
    UserSummary,
} from '../types/global/helper';
import dayjs from 'dayjs';
import { IActSummary } from '../types/global/media.types';
import produce from 'immer';
import { IUserProfile } from '../redux/user/actions';
import { SentryCapture } from '../analytics/Sentry';
import { isEmpty } from './lodash';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import { SITE_URL } from '../configurations/api/url';
import { ILink } from '../redux/links/types';
import { ICompany } from '../redux/company/types';
import html2canvas from 'html2canvas';

dayjs.extend(relativeTime);
dayjs.extend(utc);

const secretKey = CryptoJS.enc.Utf8.parse('b75524255a7f54d2726a951bb39204df'); // Convert the key to a WordArray
const iv = CryptoJS.enc.Utf8.parse('1583288699248111');

export interface IVideoEncryptor {
    [key: string]: string;
}

export function useDispatchPromise() {
    const dispatch = useDispatch();
    const callDispatch = (props: any) =>
        new Promise((resolve, reject) => {
            dispatch({ ...props, resolve, reject });
        });

    return callDispatch;
}

export const getJWtDetails = (jwt = null) => {
    const { authentication, user } = store.getState();
    const storedToken = jwt || authentication.token;
    try {
        if (storedToken) {
            const { id, exp } = jwtDecode(storedToken) as any;
            const currentUtcTimestamp = moment().valueOf();
            const timeDifference = exp * 1000 - currentUtcTimestamp;
            return {
                tokenExists: true,
                user_id: id,
                isSignedIn: new Date(exp * 1000) > new Date(Date.now()),
                token: storedToken,
                timeTillTokenExpiration: `${moment.duration(timeDifference).humanize()} left`,
                user: user.userProfile as IUserProfile,
            };
        }
        return { tokenExists: false, user_id: '', isSignedIn: false, user: null };
    } catch (error) {
        SentryCapture(error, 'error');
    }

    return { tokenExists: false, user_id: '', isSignedIn: false, user: null };
};

export const restoreInitialState = (state: any, initialState: any): any => {
    return produce(state, () => {
        // Resetting the state to its initial values
        return { ...initialState };
    });
};

export const catchAsync =
    (fn: any) =>
    (...args: any) => {
        fn(...args).catch((error: any) => console.log(error));
    };

export const isConversionRequired = () => {
    const { tokenExists, user_id } = getJWtDetails();
    return { isConversion: tokenExists, user_id };
};

export const formSignInString = (buttonText: string) => {
    const { isSignedIn } = getJWtDetails();
    if (isSignedIn) {
        return buttonText;
    }
    return `Sign in to ${buttonText.toLowerCase()}`;
};

export const getUserTalentProfileDetails = () => {
    const { talentProfile } = store.getState();
    const { currentUserTalentProfile } = talentProfile;
    let fullFields = 0;
    let percentageComplete = 0;
    const emptyFields: string[] = [];

    if (!isEmpty(currentUserTalentProfile)) {
        const numberOfFields = Object.keys(currentUserTalentProfile);
        numberOfFields.forEach(field => {
            if (!isEmpty(currentUserTalentProfile[field]) || isNumber(currentUserTalentProfile[field])) {
                fullFields += 1;
            } else {
                emptyFields.push(field);
            }
        });
        percentageComplete = (fullFields * 100) / numberOfFields.length;
    } else {
        return { profilePresent: false, percentageComplete: '0%', emptyFields: [] };
    }

    return {
        profilePresent: !isEmpty(currentUserTalentProfile),
        percentageComplete: `${percentageComplete.toFixed()}%`,
        emptyFields,
    };
};

export const showToastMessage = (
    toastMessage: JSX.Element | string = 'Notification here',
    toastType: TypeOptions = 'info',
    autoClose?: number | false,
    onClose?: (arg?: any) => void,
    onOpen?: (arg?: any) => void,
    customIcon?: JSX.Element | false,
    toastPosition?: ToastPosition,
    hideProgressBar?: boolean,
    draggable?: boolean,
    pauseOnHover?: boolean,
    closeOnClick?: boolean,
) => {
    const toastId = toast(toastMessage, {
        position: toastPosition ?? toast.POSITION.TOP_RIGHT,
        theme: 'dark',
        type: toastType,
        autoClose: autoClose ?? 5000,
        hideProgressBar: hideProgressBar,
        closeOnClick: closeOnClick,
        pauseOnHover: pauseOnHover,
        draggable: draggable,
        onClose: ({ uuid }: any) => onClose && onClose(uuid),
        onOpen: ({ uuid }: any) => onOpen && onOpen(uuid),
        /**icon: ({theme, type}) =>  <img src="url"/> */
        icon: customIcon,
        style: {
            zIndex: 999,
        },
    });

    return toastId;
};

export const formatTimeWithTimezone = (time: string, format: string = 'MMMM D, YYYY. h:mmA z') => {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return moment.tz(time, userTimeZone).format(format);
};

export const formatTimeWithToDay = (time: string, format: string = 'MMM D, YYYY') => {
    return dayjs(time).format(format);
};

export const checkDateValidity = (date: string) => {
    return moment(date).isValid();
};

export const formatNumberWithK = (number: number) => {
    if (!number) {
        return '0';
    }

    const absNumber = Math.abs(number);
    const sign = Math.sign(number);

    if (absNumber >= 1_000_000_000) {
        const formattedNumber = (sign * (absNumber / 1_000_000_000)).toFixed(2);
        return formattedNumber.replace(/\.00$/, '').replace(/(\.\d)0$/, '$1') + 'B'; // Billion
    }
    if (absNumber >= 1_000_000) {
        const formattedNumber = (sign * (absNumber / 1_000_000)).toFixed(2);
        return formattedNumber.replace(/\.00$/, '').replace(/(\.\d)0$/, '$1') + 'M'; // Million
    }
    if (absNumber >= 1_000) {
        const formattedNumber = (sign * (absNumber / 1_000)).toFixed(2);
        return formattedNumber.replace(/\.00$/, '').replace(/(\.\d)0$/, '$1') + 'K'; // Thousand
    }

    return number.toString();
};

export const daysLeftFromToday = (targetDate?: string) => {
    if (!targetDate) return null;
    const parsedTargetDate = moment(targetDate, 'YYYY-MM-DD');
    const currentDate = moment();
    const differenceInMilliseconds = parsedTargetDate.diff(currentDate);
    const daysLeft = Math.ceil(moment.duration(differenceInMilliseconds).asDays());
    return daysLeft;
};

type TFormat = 'withTimezone' | string;

export const formatDateWithTimezone = (date: string, timezone: string = 'America/New_York') => {
    const m = moment.tz(date, timezone);
    return `${m.format('dddd, MMMM D, YYYY, h:mm A')} ${m.zoneAbbr()}`;
};

export const formatDate = (targetDate?: string | Date, dateFormat: TFormat = 'YYYY') => {
    if (!targetDate) return '';
    if (dateFormat === 'withTimezone') {
        return new Date(targetDate).toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short',
        });
    }
    const year = moment(targetDate).format(dateFormat);
    return year;
};

export const createFullName = (user: UserSummary | IUserSummary, justUserName?: boolean) => {
    if (justUserName) return user && user.userName ? user.userName : '';
    return `${user && user.firstName ? user.firstName : ''} ${user && user.lastName ? user.lastName : ''}`;
};

export const createUserUrl = (user: IUserSummary | IUserProfile) => {
    if (!user || !user.userName) return '';
    return `${SITE_URL}/${user.userName}`;
};

export const convertMinuteSecondsDays = (time: any) => {
    const currentTime = dayjs();
    const eventStartTime = dayjs.utc(time).local();
    return eventStartTime.from(currentTime, true);
};

export const encryptAndGenerateVideoId = (valuesObj: IVideoEncryptor) => {
    const combinedValues = JSON.stringify(valuesObj);
    const encrypted = CryptoJS.AES.encrypt(combinedValues, secretKey, { iv: iv }).toString();
    const videoId = encrypted.slice(0, 11);
    return { encryptedData: encrypted, videoId };
};

export const decryptVideoId = (videoId: string) => {
    const encryptedData = videoId + '0'.repeat(32 - videoId.length); // Padding to ensure proper decryption
    const decrypted = CryptoJS.AES.decrypt(encryptedData, secretKey, { iv: iv });
    const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decryptedString);
};
export const randomizeArrayOrder = (arr: Array<any>) => {
    const shuffledArray = [...arr];
    function compareRandom() {
        return Math.random() - 0.5;
    }
    shuffledArray.sort(compareRandom);
    return shuffledArray;
};

export const hasNonEmptyArrayField = (object: any) => {
    for (const key in object) {
        if (object.hasOwnProperty(key)) {
            const value = object[key];
            if (Array.isArray(value) && value.length > 0) {
                return true; // Found a non-empty array field, return true
            }
        }
    }
    return false; // No non-empty array fields found, return false
};

export const filterOutNotAllowed = (obj: any, ...notAllowed: any) => {
    const newObj: any = {};
    Object.keys(obj).forEach(el => {
        if (!notAllowed.includes(el)) {
            newObj[el] = obj[el];
        }
    });
    return newObj;
};

export const onlyTheseFields = (obj: any, ...allowed: any) => {
    const newObj: any = {};
    Object.keys(obj).forEach(el => {
        if (allowed.includes(el)) {
            newObj[el] = obj[el];
        }
    });
    return Object.values(newObj).flat();
};

export const generateShareLink = (
    type: 'project' | 'user' | 'contest' | 'social' | 'playlist' | 'event' | 'audition' | 'profile' | 'company',
    doc:
        | IUserSummary
        | IProject
        | IContest
        | IActSummary
        | ILink
        | IThspianPlaylist
        | IEvent
        | IAudition
        | IWorkProfile
        | ICompany,
    noExecute?: boolean,
) => {
    const url = window.location.origin;
    let link = '';
    switch (type) {
        case 'project':
            const project = doc as IProject;
            link = `${url}/watch-share?id=${project.video_id}`;
            break;
        case 'user':
            const user = doc as IUserSummary;
            link = `${url}/@${user.userName}`;
            break;
        case 'contest':
            const contest = doc as IContest;
            if (contest.kind === 'physical') {
                link = `${url}/event/${contest.slug}`;
            } else {
                link = `${url}/campaign/${contest.slug}`;
            }
            break;
        case 'social':
            const document = doc as ILink;
            link = `${url}/links?id=${document.unique_id}`;
            break;
        case 'playlist':
            const playlist = doc as IThspianPlaylist;
            link = `${url}/watch-share?playlist=${playlist.playlist_id}`;
            break;
        case 'event':
            const event = doc as IEvent;
            link = `${url}/event/${event.slug}`;
            break;
        case 'audition':
            const audition = doc as IAudition;
            link = `${url}/audme/${audition.slug}`;
            break;
        case 'profile':
            const profile = doc as IWorkProfile;
            link = `${url}/p/${profile.created_by?.userName ?? profile.user.userName}`;
            break;
        case 'company':
            const company = doc as ICompany;
            link = `${url}/company/${company.slug}`;
            break;
        default:
            break;
    }
    if (!noExecute) {
        const tempInput = document.createElement('input');
        tempInput.value = link;
        document.body.appendChild(tempInput);
        tempInput.select();
        tempInput.setSelectionRange(0, 99999);
        navigator.clipboard.writeText(link);
        showToastMessage('Link copied', 'success');
    }
    return link;
};

export const makeArrayUniqueAndClean = (array: any[]) => {
    const uniqueArray = [];
    const uniqueIds = new Set();

    for (const obj of array) {
        if (!!obj && !uniqueIds.has(obj._id)) {
            uniqueIds.add(obj._id);
            uniqueArray.push(obj);
        }
    }

    return uniqueArray;
};

export function makePositive(number: number) {
    // Check if the number is negative
    if (number < 0) {
        // If negative, convert it to positive
        return -number;
    } else {
        // If non-negative, return as is
        return number;
    }
}

export function convertSecondsToTime(seconds: number) {
    if (seconds < 60) {
        return `0:${Math.floor(seconds).toString().padStart(2, '0')}`;
    } else {
        let hours = Math.floor(seconds / 3600);
        let remainingSeconds = seconds % 3600;

        if (remainingSeconds < 60) {
            return `${hours}:00:${Math.floor(remainingSeconds).toString().padStart(2, '0')}`;
        } else {
            let minutes = Math.floor(remainingSeconds / 60);
            let remainingSecs = Math.floor(remainingSeconds % 60);
            return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSecs.toString().padStart(2, '0')}`;
        }
    }
}

export function replacePageValue(url: string, newPageValue: number) {
    const regex = /(\?|&)page=\d+/;
    const replacement = `$1page=${newPageValue}`;
    const updatedUrl = url.replace(regex, replacement);
    return updatedUrl;
}

export function cutoffAndAddPlus(number: number, cutOffValue: number) {
    if (typeof number !== 'number' || isNaN(number)) {
        return 'Please provide a valid number';
    }

    if (number > cutOffValue) {
        return `${cutOffValue}+`;
    }
    return number;
}
interface TextPosition {
    top: number;
    left: number;
    width: number;
    height: number;
}

export function findTextPosition(containerSelector: string, searchText: string): TextPosition | null {
    const container = document.querySelector<HTMLElement>(containerSelector);
    if (!container) {
        console.error('Container element not found');
        return null;
    }

    function traverseNodes(node: Node): TextPosition | null {
        if (node.nodeType === Node.TEXT_NODE && node.textContent?.includes(searchText)) {
            const range = document.createRange();
            range.selectNodeContents(node);
            const text = node.textContent as string;
            const index = text.indexOf(searchText);

            range.setStart(node, index);
            range.setEnd(node, index + searchText.length);

            const rect = range.getBoundingClientRect();
            return {
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height,
            };
        }

        if (node.childNodes && node.childNodes.length > 0) {
            for (const childNode of Array.from(node.childNodes)) {
                const found = traverseNodes(childNode);
                if (found) {
                    return found;
                }
            }
        }

        return null;
    }

    const position = traverseNodes(container);
    if (!position) {
        console.error('Text not found in the container');
    }

    return position;
}

/** This is a hack for now to fetch the ide for documents that sometimes
 * return a string instead of populating. Will need to fix or find a permannent solution
 */
export const getIdForIbjects = (document: any) => {
    if (!document) return '';
    if (typeof document === 'string') {
        return document;
    }
    return document._id;
};

export function bytesToMB(bytes: number) {
    return (bytes / (1024 * 1024)).toFixed(0);
}

export function parseFileFormats(data: IGlobalConfigurationMediaControl, inclusionList?: string[]) {
    const supportedFormats: any = {};

    if (data && data.supported_file_formats && Array.isArray(data.supported_file_formats)) {
        data.supported_file_formats.forEach(format => {
            if (format.base && format.extension && Array.isArray(format.extension)) {
                if (
                    inclusionList &&
                    inclusionList.some(val => format.base.split('/')[0].startsWith(val.split('/')[0]))
                ) {
                    supportedFormats[format.base] = format.extension;
                }

                if (!inclusionList) {
                    supportedFormats[format.base] = format.extension;
                }
            }
        });
    }

    return supportedFormats;
}

export const getFontSize = (name?: string) => {
    const nameLength = name ? name.length : 4;
    console.log(nameLength);
    if (nameLength <= 12) {
        return 18; // Font size for shorter names
    } else {
        // Adjust the calculation based on your requirements

        console.log(Math.floor(18 / nameLength) * 18);
        return Math.floor(18 / nameLength) * 5; // Font size decreases as name length increases
    }
};

export const moveItemToIndexZero = (arr: any[], index: number) => {
    if (index >= 0 && index < arr.length) {
        const item = arr.splice(index, 1)[0];
        arr.unshift(item);
    } else {
        console.log('Index out of range.');
    }
    return arr;
};

export function getFilteredRoutes(routes: any, pathsToHide: any) {
    // const filteredRoutes = Object.values(routes).filter(route => !pathsToHide.includes(route));
    return ['/search'];
    // return filteredRoutes;
}

export const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
export const isAndroid = /Android/.test(navigator.userAgent);

// utils.ts
// browser.enum.ts

export enum Browser {
    CHROME = 'Google Chrome',
    FIREFOX = 'Mozilla Firefox',
    SAFARI = 'Apple Safari',
    IE = 'Internet Explorer',
    UNKNOWN = 'Unknown',
}

/**
 * Utility function to detect the browser.
 * @returns {Browser} The name of the detected browser.
 */
export function detectBrowser(): Browser {
    const userAgent = window.navigator.userAgent;

    if (userAgent.indexOf('Chrome') !== -1) {
        return Browser.CHROME;
    } else if (userAgent.indexOf('Firefox') !== -1) {
        return Browser.FIREFOX;
    } else if (userAgent.indexOf('Safari') !== -1) {
        return Browser.SAFARI;
    } else if (userAgent.indexOf('MSIE') !== -1 || userAgent.indexOf('Trident/') !== -1) {
        return Browser.IE;
    }

    return Browser.UNKNOWN;
}

export function ensureHttps(url: string) {
    if (!url) return;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
        return `https://${url}`;
    }
    return url;
}
export const formatTime = (seconds: number) => {
    if (!seconds) return '00:00';
    const totalSeconds = Math.floor(seconds);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    if (totalSeconds < 60) {
        return `00:${String(secs).padStart(2, '0')}`;
    } else if (totalSeconds < 3600) {
        return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    } else {
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
};

export const parsePlaylist = (projectList?: IProject[]): INuevoPlaylist[] => {
    const model: INuevoPlaylist[] = [];

    if (projectList && projectList.length > 0) {
        projectList.forEach(project => {
            const temp: INuevoPlaylist = {
                duration: formatTime(project.metadata?.duration!),
                sources: [
                    {
                        src: project.hls!,
                        type: 'application/x-mpegURL',
                    },
                ],
                thumb: project.feature_image,
                title: project.title,
                url: project.hls!,
                slideImage: project.feature_image,
            };

            model.push(temp);
        });
    }

    return model;
};

export const parseUpNextList = (projectList?: IProject[], currentProject?: IProject): IUpNext[] | undefined => {
    const list = [...projectList!];
    if (!list) return;
    // Find the index of the currentProject in the list
    // const currentProjectIndex = list.findIndex(project => !!currentProject && project._id === currentProject._id);

    // If the currentProject is in the list, splice it out
    // if (currentProjectIndex !== -1) {
    //     list.splice(currentProjectIndex, 1);
    // }

    // If the currentProjectIndex was found, adjust the start index to start from the next item
    // const startIndex = currentProjectIndex !== -1 ? currentProjectIndex : 0;

    // Create a new list starting from the next item after the currentProject index, up to 20 items
    // const limitedList = list.slice(startIndex, startIndex + 20);

    const finalList: IUpNext[] = [];
    list.forEach(project => {
        if (project.hls && project._id !== currentProject?._id) {
            const model: IUpNext = {
                duration: formatTime(project.metadata?.duration!),
                poster: project.thumbnail!,
                sources: [
                    {
                        src: project.hls!,
                        type: 'application/x-mpegURL',
                    },
                ],
                title: project.title,
                project: project,
            };
            finalList.push(model);
        }
    });

    return finalList;
};

export const formQueryParams = (...params: Record<string, string>[]): string => {
    if (params && params.length === 0) {
        return '';
    }

    let queryString = '?';
    if (params) {
        params.forEach(param => {
            if (typeof param === 'object' && param !== null) {
                for (const key in param) {
                    if (param.hasOwnProperty(key) && param[key] !== undefined) {
                        queryString += `${encodeURIComponent(key)}=${encodeURIComponent(param[key])}&`;
                    }
                }
            }
        });
    }

    return queryString.length > 1 ? queryString.slice(0, -1) : '';
};

export const clearAllCookies = () => {
    const allCookies = Cookies.get(); // Get all cookies
    console.log(allCookies);
    Object.keys(allCookies).forEach(cookieName => {
        Cookies.remove(cookieName);
    });
};

export const moveElement = (array: any[], fromIndex: number, toIndex: number) => {
    if (toIndex >= array.length) {
        toIndex = array.length - 1; // Prevent out-of-bound errors
    }
    const element = array[fromIndex];
    array.splice(fromIndex, 1); // Remove element from the fromIndex
    array.splice(toIndex, 0, element); // Insert element at the toIndex
    return array;
};

// Format the date with an ordinal suffix
export function formatWithOrdinal(date?: Date | string, noYear?: boolean) {
    const day = moment(date).date();
    const formats = noYear ? `MMM Do` : `MMM Do, YYYY`;
    const suffix =
        day % 10 === 1 && day !== 11
            ? 'st'
            : day % 10 === 2 && day !== 12
            ? 'nd'
            : day % 10 === 3 && day !== 13
            ? 'rd'
            : 'th';
    return moment(date).format(formats).replace('Do', `${day}${suffix}`);
}

export function parseFee(fee: string): number {
    if (!fee) return 0;
    return parseFloat(String(fee).replace(/[^0-9.-]+/g, ''));
}

// Function to get min and max compensation with original formatted compensation string
export function getMinMaxFee(fees: FeeStructure[]): { min_fee: string; max_fee: string } {
    let minFeeValue = Infinity;
    let maxFeeValue = -Infinity;
    let minFee = '';
    let maxFee = '';

    if (!fees || isEmpty(fees)) {
        return {
            min_fee: '',
            max_fee: '',
        };
    }

    // Iterate through the roles to find min and max compensation
    fees.forEach(fee => {
        const parsedCompensation = parseFee(fee.fee);

        // Check for min compensation
        if (parsedCompensation < minFeeValue) {
            minFeeValue = parsedCompensation;
            minFee = fee.fee; // Store the original formatted string for min compensation
        }

        // Check for max compensation
        if (parsedCompensation > maxFeeValue) {
            maxFeeValue = parsedCompensation;
            maxFee = fee.fee; // Store the original formatted string for max compensation
        }
    });

    // Return the original formatted compensation values (min and max)
    return {
        min_fee: minFee,
        max_fee: maxFee,
    };
}

export const getFileExtension = (file: File | string) => {
    if (typeof file === 'string') {
        const matches = file.match(/^data:(.+);base64,/);

        if (matches && matches.length > 1) {
            // Extract the MIME type from the metadata and return the file extension
            const mimeType = matches[1];
            // Get the file extension by splitting the MIME type
            return mimeType.split('/')[1];
        }
    }

    if (typeof file === 'object') {
        const fileName = file.type;
        return fileName.substring(fileName.lastIndexOf('/') + 1).toLowerCase();
    }

    return 'jpeg';
};

export function transformObjectOrArray(data: any) {
    function transform(value: any): any {
        if (Array.isArray(value)) {
            // Deep clone and process each element of the array recursively
            return value.map(transform);
        } else if (value && typeof value === 'object' && !Array.isArray(value)) {
            const keys = Object.keys(value);
            // If the object has `label` and `value`, transform it
            if (keys.length === 2 && keys.includes('label') && keys.includes('value')) {
                return value.value; // Replace object with the value string
            } else {
                // Deep clone and recursively transform nested objects
                const clonedObject: any = {};
                for (const [key, nestedValue] of Object.entries(value)) {
                    clonedObject[key] = transform(nestedValue);
                }
                return clonedObject;
            }
        } else {
            // If not an object or array, return the value as is
            return value;
        }
    }

    // Clone the input data and start transformation
    return transform(JSON.parse(JSON.stringify(data)));
}

export function timeUntil(date: string) {
    const now = moment();
    const targetDate = moment(date);

    if (targetDate.isBefore(now)) {
        return 'Already started';
    }

    const differenceInDays = targetDate.diff(now, 'day');

    if (differenceInDays < 3) {
        return 'Starting soon';
    }

    if (differenceInDays < 30) {
        return `Starting in ${differenceInDays} days`;
    }

    const differenceInMonths = targetDate.diff(now, 'month');

    if (differenceInMonths < 12) {
        return `Starting in ${differenceInMonths} month${differenceInMonths > 1 ? 's' : ''}`;
    }

    const differenceInYears = targetDate.diff(now, 'year');

    return `Starting in ${differenceInYears} year${differenceInYears > 1 ? 's' : ''}`;
}

export function filterUploadFiles(payload: Record<string, any>, uploadFields: string[], bodyFields: string[]) {
    const uploads: Record<string, any[]> = {};
    const body: Record<string, any> = {};

    Object.keys(payload).forEach(key => {
        if (uploadFields.includes(key)) {
            uploads[key] = Array.isArray(payload[key]) ? payload[key] : [payload[key]];
        } else if (bodyFields.includes(key)) {
            body[key] = payload[key];
        }
    });

    return { body, uploads };
}

export function parseProfile(data: any): { body: any; uploads: any } {
    const parsedData: Record<string, any> = {};
    const uploads: Record<string, any> = {};

    const extractValue = (input: any): any => {
        if (typeof input === 'object' && input !== null) {
            if ('label' in input && 'value' in input) {
                return input.value;
            }
            if (Array.isArray(input)) {
                return input.map(item => extractValue(item));
            }
            return Object.keys(input).reduce((acc, key) => {
                acc[key] = extractValue(input[key]);
                return acc;
            }, {} as Record<string, any>);
        }
        return input;
    };

    for (const key in data) {
        if (data.hasOwnProperty(key) && !['profile_picture', 'photos'].includes(key)) {
            const value = data[key];

            if (key === 'demo_reel' || key === 'audio_reel') {
                // Ensure it returns an array of IDs
                parsedData[key] = Array.isArray(value) ? value.map(item => item['_id']) : [];
            } else if (key === 'singleRawFile') {
                uploads['profile_picture'] = data['singleRawFile'];
            } else if (key === 'age_range' && Array.isArray(value) && value.length === 2) {
                parsedData['age_from'] = value[0];
                parsedData['age_to'] = value[1];
            } else if (key === 'rawFiles') {
                uploads['photos'] = data['rawFiles'];
            } else {
                parsedData[key] = extractValue(value);
            }
        }
    }

    return { body: parsedData, uploads };
}

export function reverseParseProfile(body: any): any {
    if (!body) return {};

    const restoredData: Record<string, any> = { ...body };

    // Restore age_range
    if ('age_from' in body && 'age_to' in body) {
        restoredData['age_range'] = [body.age_from, body.age_to];
        delete restoredData['age_from'];
        delete restoredData['age_to'];
    }

    // Parse profile_picture as a string
    if (body.profile_picture) {
        restoredData['profile_picture'] = body.profile_picture;
    }

    // Store photos as an array of strings
    if (Array.isArray(body.photos)) {
        restoredData['photos'] = body.photos;
    }

    if (body.hair_type) {
        restoredData['hair_type'] = body.hair_type;
    }

    if (body.language_spoken) {
        restoredData['language_spoken'] = body.language_spoken;
    }

    return restoredData;
}

// Currency symbols mapping
export const currencySymbols: Record<string, string> = {
    USD: '$',
    NGN: '₦',
    EUR: '€',
    GBP: '£',
};

// Function to remove non-numeric characters (currency symbols, commas, etc.)
export function parseCompensation(compensation: string | number): { symbol: string; amount: number } {
    if (!compensation) return { symbol: '', amount: 0 };
    const match = String(compensation).match(/([^\d.,-]+)/);
    const symbol = match ? match[1].trim() : '';
    const amount = parseFloat(String(compensation).replace(/[^0-9.-]+/g, ''));

    return { symbol, amount };
}

// Function to get min and max compensation along with currency
export function getMinMaxCompensation(roles: IRole[]): {
    min: number;
    max: number;
    currency?: string;
    symbol?: string;
} {
    let minCompensation = Infinity;
    let maxCompensation = -Infinity;
    let currency: string | undefined;
    let currentSymbol: string | undefined;

    roles.forEach(role => {
        const { amount, symbol } = parseCompensation(role.compensation);
        currentSymbol = symbol;
        if (!isNaN(amount)) {
            if (amount < minCompensation) {
                minCompensation = amount;
                currency = role.currency; // Store currency from the lowest value role
            }
            if (amount > maxCompensation) {
                maxCompensation = amount;
            }
        }
    });

    // If no valid compensation was found, return default values
    if (minCompensation === Infinity || maxCompensation === -Infinity) {
        return { min: 0, max: 0 };
    }

    return { min: minCompensation, max: maxCompensation, currency, symbol: currentSymbol };
}

export function getPayRangeText(roles: IRole[]) {
    if (!Array.isArray(roles) || roles.length === 0) return '';

    // Extract numeric values + symbols
    const amounts = roles
        .map(r => {
            if (!r.compensation || !r.currency) return null;
            const value = parseFloat(String(r.compensation).replace(/[^\d.]/g, ''));
            const symbol = String(r.compensation).replace(/[\d.,]/g, '') || r.currency;
            return isNaN(value) ? null : { value, symbol };
        })
        .filter(Boolean);

    if (amounts.length === 0) return '';

    const values: number[] = amounts.map(a => a && a.value) as number[];
    const symbols = [...new Set(amounts.map(a => a && a.symbol))];

    // If all symbols match, use it, otherwise fallback to currency field
    const symbol = symbols.length === 1 ? symbols[0] : '';

    const min = Math.min(...values);
    const max = Math.max(...values);

    // format numbers with commas (or locale-specific)
    const formatter = new Intl.NumberFormat('en-US');

    if (min !== max) {
        return `Pays between ${symbol}${formatter.format(min)} and ${symbol}${formatter.format(max)}`;
    } else if (values.length > 1 && symbol) {
        return `Pays any of ${values.map(v => symbol + formatter.format(v)).join(', ')}`;
    } else {
        return `Pays ${symbol}${formatter.format(values[0])}`;
    }
}

// Function to format currency display
export function formatCurrency(amount: number, currency?: string, symbol?: string, cents?: boolean): string {
    let currAmount = amount;
    if (cents) {
        currAmount = amount / 100;
    }
    if (!currency) return currAmount.toString();
    const parsedSymbol = symbol ? symbol : currencySymbols[currency] || currency;
    return `${parsedSymbol}${currAmount.toLocaleString()}`;
}
export const MAP_LIBRARIES: any = ['places'];

export const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export function capitalizeWords(sentence?: string) {
    if (!sentence) return '';
    return sentence
        .split(' ')
        .map(word => {
            if (word.length === 0) return '';
            return word[0].toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(' ');
}

/**
 * 
 * @param element import React, { useEffect, useRef } from 'react';
import { captureComponentAsJPEGFile } from './captureComponentAsJPEGFile';

const AutoCaptureComponent: React.FC = () => {
  const captureRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const captureAndUpload = async () => {
      if (!captureRef.current) return;

      try {
        const jpegFile = await captureComponentAsJPEGFile(captureRef.current);

        // Upload the file
        const formData = new FormData();
        formData.append('file', jpegFile);

        const response = await fetch('/upload', {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) throw new Error('Upload failed');
        console.log('Upload successful');
      } catch (error) {
        console.error('Error capturing or uploading:', error);
      }
    };

    captureAndUpload();
  }, []); // Only run once on mount

  return (
    <div>
      <div ref={captureRef} style={{ backgroundColor: '#f0f0f0', padding: '1rem' }}>
        <h2>Auto-Capture This</h2>
        <p>This component will be captured and uploaded when the component mounts.</p>
      </div>
    </div>
  );
};

export default AutoCaptureComponent;
 * @param fileName 
 * @returns 
 */
export async function captureComponentAsJPEGFile(element: HTMLElement, fileName = 'snapshot.jpg'): Promise<File> {
    if (!element) {
        throw new Error('Element is null or undefined');
    }

    const canvas = await html2canvas(element, {
        backgroundColor: '#fff',
        useCORS: true,
        ignoreElements: el => {
            return el.hasAttribute('data-ignore-capture') || el.classList.contains('no-capture');
        },
        scale: 1,
        width: element.scrollWidth,
        height: element.scrollHeight,
        scrollX: 0,
    });

    return new Promise<File>((resolve, reject) => {
        canvas.toBlob(
            blob => {
                if (!blob) {
                    reject(new Error('Canvas toBlob failed'));
                    return;
                }
                const file = new File([blob], fileName, { type: 'image/jpeg' });
                resolve(file);
            },
            'image/jpeg',
            0.95,
        );
    });
}

export function getCurrencySymbol(currencyCode: string): string {
    try {
        // Use Intl.NumberFormat to format a dummy value and extract the symbol
        const formatter = new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: currencyCode.toUpperCase(),
            currencyDisplay: 'narrowSymbol',
        });

        const parts = formatter.formatToParts(1);
        const symbolPart = parts.find(p => p.type === 'currency');

        // Return the detected symbol, or fall back to the code itself
        return symbolPart ? symbolPart.value : currencyCode.toUpperCase();
    } catch {
        // Invalid currency code or unsupported locale
        return currencyCode.toUpperCase();
    }
}
